import type { EditRequest, Fixture } from '../../../../dorian-shared/types/episodeScript';
import { sceneOfBranch } from './compactGraph';
import type { GraphNode } from './compactGraph';
import { humanise } from './publicNames';
import type { PublicNames } from './publicNames';

/**
 * Basil v0.2, P4 — a Changes row shows the WORDS, before and after.
 *
 * The row used to lead with counts: "Meant to change 1 scene, 1 line and 0 answers." That is a true
 * sentence about a request and a useless one about a sentence a writer wrote. What they want to
 * check before pressing Apply is the thing they changed, so the row shows exactly that:
 *
 * > Lilly's line in intro
 * > Tea first. Then the door. → Tea can wait. Open the door.
 *
 * The BEFORE comes from the read fixture — the bytes the script was decompiled from — and the AFTER
 * from the request the compiler built. Neither is re-derived from the other, so a row can only say
 * a change happened when the two actually differ.
 */

/** One row's words: whose line, and what it said before and after. */
export interface ChangeWords {
  /** `Lilly's line in intro` */
  title: string;
  /** the text as the episode holds it now. */
  before: string;
  /** the text the request would put there. */
  after: string;
}

/** How a change reads when the thing it changes has no speaker. */
export const NARRATION_TITLE = 'The narration';

/** How an answer reads. An answer belongs to a choice, not to a character. */
export const ANSWER_TITLE = 'An answer';

const stepOf = (fixture: Fixture | null | undefined, branchId: unknown, stepId: unknown) => {
  const branch = (fixture?.branches ?? []).find((one) => String(one.id) === String(branchId));

  if (!branch) {
    return null;
  }

  return (branch.steps ?? []).find((one) => String(one.id) === String(stepId)) ?? null;
};

/** `Lilly's line` plus the scene the script spells, where the script spells one. */
function withScene(title: string, scene: string | null): string {
  return scene ? `${title} in ${scene}` : title;
}

/**
 * Whose line this is, in the writer's words.
 *
 * A character with a name reads as `Lilly's line`. A character the book cannot name reads as
 * `A character's line` — the margin note says the rest, and no id is printed here either.
 */
export function speakerTitle(names: PublicNames, characterId: unknown): string {
  if (characterId == null) {
    return NARRATION_TITLE;
  }
  const human = humanise(names, 'characters', `C${String(characterId)}`);

  return `${human.text}'s line`;
}

/**
 * The words one request changes, or null where the request does not change words at all.
 *
 * Only the two sendable ops have words to show: a step's text and an answer's text. Anything else
 * keeps the phrase it already had, because it is not a change to a sentence.
 */
export function changeWords(
  request: EditRequest,
  fixture: Fixture | null | undefined,
  names: PublicNames,
  headings: Map<string, string>,
  graph: GraphNode[],
): ChangeWords | null {
  const op = String((request as { op?: string }).op ?? '');
  const view = request as unknown as {
    branchId?: unknown; stepId?: unknown; answerId?: unknown; text?: unknown;
  };
  const after = typeof view.text === 'string' ? view.text : '';
  const scene = sceneOfBranch(headings, graph, view.branchId ?? null);
  const step = stepOf(fixture, view.branchId, view.stepId);

  if (op === 'set_step_text') {
    if (!step) {
      return null;
    }

    return {
      title: withScene(speakerTitle(names, step.characterId ?? null), scene),
      before: String(step.text ?? ''),
      after,
    };
  }

  if (op === 'set_answer_text') {
    if (!step) {
      return null;
    }
    const answer = (step.answers ?? []).find((one) => String(one.id) === String(view.answerId));

    if (!answer) {
      return null;
    }

    return {
      title: withScene(ANSWER_TITLE, scene),
      before: String(answer.text ?? ''),
      after,
    };
  }

  return null;
}

/** The before and after on one line, as the row shows them. */
export function beforeAfter(words: ChangeWords): string {
  return `${words.before} → ${words.after}`;
}
